import { Business, User, Account } from '@/types';
import { fetchApiV2 } from '@/fetch';

export type LoginRequest = {
  email: string;
  password: string;
  canvasHash: string;
  fingerprintHash: string;
  fingerprintData: Record<string, unknown>;
  heeetData?: Record<string, unknown>;
  pardotVisitorId?: string;
};

export type SecondFactorResponse = {
  mfa_type: 'authenticator' | 'phone_number';
  is_phone_number_verified: boolean;
  user_id: string;
};

export type LoginSuccessResponse = {
  access_token: string;
  refresh_token: string;
  user: User;
  business: Business;
};

export type LoginResponse = SecondFactorResponse | LoginSuccessResponse;

export const login = async (request: LoginRequest) => {
  return await fetchApiV2<LoginRequest, Record<string, unknown>, LoginResponse>(
    'POST',
    '/api/auth/email',
    {
      public: true,
      request
    }
  );
};

export const logout = async (refreshToken: string) => {
  return await fetchApiV2<
    Record<string, unknown>,
    Record<string, unknown>,
    Record<string, unknown>
  >('DELETE', '/api/auth/token', {
    request: {
      token: refreshToken
    }
  });
};

export type RefreshTokenResponse = {
  access_token: string;
};

export const refreshAccessToken = async (refreshToken: string) => {
  return await fetchApiV2<
    Record<string, unknown>,
    Record<string, unknown>,
    RefreshTokenResponse
  >('POST', '/api/auth/token/refresh', {
    public: true,
    request: {
      refresh_token: refreshToken
    }
  });
};

export type CreateTotpDeviceResponse = {
  base32: string;
  otpauthUrl: string;
};

export const createTotpDevice = async () => {
  return await fetchApiV2<
    Record<string, unknown>,
    Record<string, unknown>,
    CreateTotpDeviceResponse
  >('GET', '/api/setup_mfa', {});
};

export type VerifyTotpDeviceRequest = {
  auth_token: string;
  mfa_authentication_secret: {
    base32: string;
  };
};

export type VerifyTotpDeviceResponse = {
  backup_codes: string[];
};

export const verifyTotpDevice = async (request: VerifyTotpDeviceRequest) => {
  return await fetchApiV2<
    VerifyTotpDeviceRequest,
    Record<string, unknown>,
    VerifyTotpDeviceResponse
  >('POST', '/api/v2/user/mfa-app', {
    request
  });
};

export type VerifyCodeRequest = {
  email: string;
  password: string;
  canvas_hash: string;
  auth_token: string;
};

export type VerifyCodeResponse = {
  access_token: string;
  refresh_token: string;
  user: User;
  business: Business;
};

export const verifyTotpCode = async (request: VerifyCodeRequest) => {
  return await fetchApiV2<
    VerifyCodeRequest,
    Record<string, unknown>,
    VerifyCodeResponse
  >('POST', '/api/authorize_authenticator_login!', {
    public: true,
    request
  });
};

export const deleteOtp = async (authToken: string, otpType: string) => {
  return await fetchApiV2<
    Record<string, unknown>,
    Record<string, unknown>,
    Record<string, unknown>
  >('DELETE', '/api/v2/users/2fa', {
    request: {},
    headers: {
      'X-Otp-Token': authToken,
      'X-Otp-Type': otpType
    }
  });
};

export type CheckBackupCodesExistenceResponse = {
  is_exist: boolean;
};

export const checkBackupCodesExistence = async (userId: string) => {
  return await fetchApiV2<
    Record<string, unknown>,
    Record<string, unknown>,
    CheckBackupCodesExistenceResponse
  >('GET', `/api/mfa_backup_code_existence/${userId}`, { public: true });
};

export type VerifyBackupCodeRequest = {
  email: string;
  password: string;
  canvas_hash: string;
  backup_code: string;
};

export type VerifyBackupCodeResponse = {
  access_token: string;
  refresh_token: string;
  user: User;
  business: Business;
};

export const verifyBackupCode = async (request: VerifyBackupCodeRequest) => {
  return await fetchApiV2<
    VerifyBackupCodeRequest,
    Record<string, unknown>,
    VerifyBackupCodeResponse
  >('PATCH', '/api/mfa_backup_code', {
    public: true,
    request
  });
};

export type OtpLoginRequest = {
  email: string;
  password: string;
  type: string;
};

export type OtpLoginResponse = {
  recipient: string;
};

export const sendOtpLogin = async (request: OtpLoginRequest) => {
  return await fetchApiV2<
    OtpLoginRequest,
    Record<string, unknown>,
    OtpLoginResponse
  >('POST', '/api/v2/auth/otp-login', {
    public: true,
    request
  });
};

export type OtpLoginVerificationRequest = {
  email: string;
  password: string;
  canvas_hash: string;
  otp_token: string;
  otp_type: string;
};

export type OtpLoginVerificationResponse = {
  access_token: string;
  refresh_token: string;
  user: User;
  business: Business;
};

export const verifyOtpLogin = async (request: OtpLoginVerificationRequest) => {
  return await fetchApiV2<
    OtpLoginVerificationRequest,
    Record<string, unknown>,
    OtpLoginVerificationResponse
  >('POST', '/api/v2/auth/authorize-otp-login', {
    public: true,
    request
  });
};

export type RegisterNewAccountRequest = {
  full_name: string;
  email: string;
  password: string;
  captcha: string;
  registration_path: string;
  canvas_hash: string;
  fingerprint_hash: string;
  fingerprint_data: Record<string, unknown>;
  pardot_visitor_id?: string;
  heeet_object?: Record<string, unknown>;
  preferred_language?: string;
};

export type RegisterNewAccountResponse = {
  access_token: string;
  refresh_token: string;
  user: User;
  account: Account;
};

export const registerNewAccount = async (
  request: RegisterNewAccountRequest
) => {
  return await fetchApiV2<
    RegisterNewAccountRequest,
    Record<string, unknown>,
    RegisterNewAccountResponse
  >('POST', '/api/v2/accounts-platform/dashboard/users/registration', {
    public: true,
    request
  });
};

export type RegisterNewIMAccountRequest = {
  business_email: string;
  business_name: string;
  creator_full_name: string;
  creator_email: string;
  creator_phone_number: string;
  creator_password: string;
  canvas_hash: string;
  preferred_language?: string;
  captcha: string;
};

export type RegisterNewIMAccountResponse = {
  access_token: string;
  refresh_token: string;
  user: User;
  account: Business;
};

export const registerNewIMAccount = async (
  request: RegisterNewIMAccountRequest
) => {
  return await fetchApiV2<
    RegisterNewIMAccountRequest,
    Record<string, unknown>,
    RegisterNewIMAccountResponse
  >('POST', '/api/businesses', {
    public: true,
    request
  });
};
